/**
 * The sharer is told where they acted (Phase 2, increment 5). One outcome
 * line, one vocabulary, derived from the LAST status the client fetched —
 * never from a client-side guess — and one table of doors, so that exactly
 * one retry shows per surface. Mirrored in mobile's `lib/publishOutcome.ts`
 * with the same table and the same tests.
 *
 * Why the doors are a table and not the toggle (design decision 1): a failed
 * or expired candidate changes no visibility, so the ordinary toggle on a
 * still-`public` plan would flip it to `private` — a withdrawal — while a
 * PATCH `public` on a plan shared from a completed trip (which left the
 * itinerary `private`/`link`) would mint a slug and expose the live itinerary
 * page, a side effect the sharer never asked for. The status carries no
 * origin, but the surface and the visibility do.
 */
import type { PublishStatus } from '../services/plansApi';

export type Tone = 'neutral' | 'attention' | 'good';
export interface OutcomeLine {
  text: string;
  tone: Tone;
  /** `fix`: there are `problems` to list. `retry`: the surface's own door applies. */
  action?: 'fix' | 'retry';
}

/** The states in which a candidate is still being judged. */
export const LIVE_STATES: readonly string[] = ['publishing', 'awaiting_owner'];
/** What a restore tells the owner when a publish queued before the delete is
 *  still pending (private provenance, decision 10). One sentence and no
 *  instruction: on a completed-trip share the plan stays `private`/`link`, so
 *  the Share toggle already reads off and pressing it would STAGE a publish,
 *  not cancel one. A withdrawn row is not mentioned (owner decision 1). */
export const RESTORE_PENDING_NOTICE = 'Publishing to Explore is still pending for this plan.';

/** The restore notice for the `publish` a restore response carries, or null.
 *  The field is optional — a rolling deploy can answer without it — and its
 *  absence renders nothing rather than throwing. */
export function restoreNotice(publish: PublishStatus | null | undefined): string | null {
  return publish && LIVE_STATES.includes(publish.state) ? RESTORE_PENDING_NOTICE : null;
}

/** The two failures a sharer can act on. `unpublished` is NOT one of them. */
export const FAILED_STATES: readonly string[] = ['needs_fix', 'incomplete'];

/** Coarse and relative ("since 3 days ago"); never a clock the reader must
 *  convert. A malformed timestamp yields nothing rather than "NaN days". */
export function relativeSince(iso: string, now: Date = new Date()): string | null {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const mins = Math.max(0, Math.round((now.getTime() - t) / 60_000));
  if (mins < 60) return 'just now';
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

function inExploreLine(status: PublishStatus, now?: Date): OutcomeLine {
  const since = status.promoted_at ? relativeSince(status.promoted_at, now) : null;
  return { text: since ? `In Explore since ${since}` : 'In Explore', tone: 'good' };
}

/** The lines for one status: the FACT ("In Explore") first when the plan is
 *  published while a replacement is still being judged or has failed — the
 *  spec's re-publish rule leaves the approved version visible — then the
 *  NEWS. `none` renders nothing. `visibility` (when the caller has it) names
 *  the completed-trip origin: a POST-staged candidate leaves the itinerary
 *  not public, so OFF + "Publishing" is two doors into one target, and the
 *  line says so. */
export function outcomeLines(
  status: PublishStatus,
  opts: { visibility?: string | null; now?: Date } = {},
): OutcomeLine[] {
  const lines: OutcomeLine[] = [];
  const fromCompletedTrip = opts.visibility !== undefined && opts.visibility !== 'public';
  const origin = fromCompletedTrip ? ' · shared from a completed trip' : '';
  if (status.published && status.state !== 'published') lines.push(inExploreLine(status, opts.now));
  switch (status.state) {
    case 'publishing':
      lines.push({ text: `Publishing — we check it first${origin}`, tone: 'neutral' });
      break;
    case 'awaiting_owner':
      lines.push({ text: `Waiting for the owner's review${origin}`, tone: 'neutral' });
      break;
    case 'needs_fix': {
      const problems = status.problems || [];
      if (problems.length === 0) {
        // What an OWNER REJECTION looks like: failed with every check passed
        // and the owner's words in `reason`.
        lines.push({ text: status.reason || "Didn't pass", tone: 'attention' });
      } else {
        const labels = problems.map((p) => p.label).join(', ');
        const first = problems[0].detail;
        lines.push({ text: first ? `Didn't pass ${labels}: ${first}` : `Didn't pass ${labels}`, tone: 'attention', action: 'fix' });
      }
      break;
    }
    case 'incomplete':
      lines.push({ text: "Didn't complete — try again", tone: 'attention', action: 'retry' });
      break;
    case 'published':
      lines.push(inExploreLine(status, opts.now));
      break;
    case 'withdrawn':
      lines.push({ text: 'Not in Explore', tone: 'neutral' });
      break;
    case 'unpublished':
      lines.push({ text: status.reason || 'Removed from Explore by the owner', tone: 'attention' });
      break;
    default:
      break;
  }
  return lines;
}

/** The share SHEET's door (web `ShareSheet`, mobile `PublishSheet`).
 *  `try_again`: the explicit PATCH `public` — only while the itinerary IS
 *  public, so it can never be the slug-minting, page-exposing PATCH on a
 *  plan shared from a completed trip. `toggle`: the ordinary toggle, whose
 *  label says it makes the plan public. `none`: `unpublished` — the line
 *  says what happened and offers no action; a re-stage would park under the
 *  flag and churn the owner's queue. */
export function sheetDoor(status: PublishStatus | null | undefined, visibility: string | null | undefined): 'try_again' | 'toggle' | 'none' | 'unknown' {
  // UNKNOWN is not "toggle": until the status has been read, the ordinary
  // toggle on a still-public plan is the withdrawal PATCH this table exists to
  // prevent, and `unpublished` must show no door at all (external round 484).
  // The surfaces render the toggle DISABLED for it, so the sheet's shape does
  // not jump when the answer arrives.
  if (!status) return 'unknown';
  if (status.state === 'unpublished') return 'none';
  if (FAILED_STATES.includes(status.state) && visibility === 'public') return 'try_again';
  return 'toggle';
}

export const MIN_COMPLETED_TRIP_EDITS = 3;

export interface CompletedTripInput {
  sourcePlan: number | null | undefined;
  endDate: string | null | undefined;
  /** The per-itinerary "Not now" key, read by the caller. */
  dismissed: boolean;
  shared: boolean;
  editCount: number;
  visibility: string | null | undefined;
  state: string;
  now?: Date;
}

/** The completed-trip share's WHOLE rule (design decision 6), one definition
 *  for the page's strip, the modal and mobile's prompt. `open`: the first
 *  offer — web opens its modal; mobile shows the strip (never an auto-open).
 *  `available`: the "Not now" key has yielded to a failure, so the strip
 *  renders with its button and nothing opens by itself. `none`: no prompt. */
export function completedTripPrompt(input: CompletedTripInput): 'open' | 'available' | 'none' {
  if (!input.sourcePlan || !input.endDate) return 'none';
  const tripEnd = new Date(input.endDate + 'T23:59:59');
  if (Number.isNaN(tripEnd.getTime()) || tripEnd >= (input.now || new Date())) return 'none';
  if (input.shared) return 'none';
  if (input.editCount < MIN_COMPLETED_TRIP_EDITS) return 'none';
  // A public plan's publish belongs to the sheet; a second POST would stage
  // the same target over the sheet's door.
  if (input.visibility === 'public') return 'none';
  if (input.state === 'unpublished') return 'none';
  if (!input.dismissed) return 'open';
  return FAILED_STATES.includes(input.state) ? 'available' : 'none';
}
